import { useEffect, useState } from 'react'
import paymentService from '../utils/paymentService'
import type { SubscriptionStatusResponse } from '../types'

interface SubscriptionHookReturn {
  hasActiveSubscription: boolean
  isLoading: boolean
  subscriptionData: SubscriptionStatusResponse | null
  checkSubscription: () => Promise<void>
  canAccessFeature: boolean
  requiresUpgrade: boolean
  restrictionMessage: string
}

/**
 * Hook to check if user has an active subscription
 * Uses the recommended /users/subscription-status endpoint
 * Returns comprehensive subscription status and loading state
 */
export const useSubscription = (): SubscriptionHookReturn => {
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionStatusResponse | null>(null)

  const checkSubscription = async () => {
    try {
      setIsLoading(true)
      const status = await paymentService.getSubscriptionStatus()
      
      setSubscriptionData(status)
      setHasActiveSubscription(status.hasActiveSubscription)
    } catch (error) {
      console.error('Failed to check subscription:', error)
      setHasActiveSubscription(false)
      setSubscriptionData(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    // Check if user is authenticated before fetching
    const token = localStorage.getItem('accessToken')
    if (token) {
      checkSubscription()
    } else {
      setIsLoading(false)
    }
  }, [])

  return {
    hasActiveSubscription,
    isLoading,
    subscriptionData,
    checkSubscription,
    canAccessFeature: subscriptionData?.restrictions?.canAccessFeature ?? false,
    requiresUpgrade: subscriptionData?.restrictions?.requiresUpgrade ?? false,
    restrictionMessage: subscriptionData?.restrictions?.message ?? '',
  }
}

/**
 * Hook to require payment for a feature
 * Shows payment prompt if user doesn't have active subscription or can't access feature
 * Uses subscription restrictions from the API
 */
export const useRequirePayment = (feature: string, requiredPlan?: string) => {
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false)
  const { 
    hasActiveSubscription, 
    isLoading, 
    canAccessFeature, 
    requiresUpgrade,
    restrictionMessage,
    subscriptionData 
  } = useSubscription()

  const requirePayment = (): boolean => {
    if (isLoading) {
      return false // Don't block while loading
    }

    // Check if user has active subscription and can access the feature
    if (!hasActiveSubscription || !canAccessFeature) {
      setShowPaymentPrompt(true)
      return false // Block access
    }

    return true // Allow access
  }

  const closePrompt = () => {
    setShowPaymentPrompt(false)
  }

  return {
    requirePayment,
    showPaymentPrompt,
    closePrompt,
    hasActiveSubscription,
    isLoading,
    feature,
    requiredPlan,
    canAccessFeature,
    requiresUpgrade,
    restrictionMessage,
    subscriptionDetails: subscriptionData?.subscription || null,
    lastPayment: subscriptionData?.lastPayment || null,
  }
}
